import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Building2,
  Globe,
  DollarSign,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Percent,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Coins,
  FileText,
  ExternalLink,
} from "lucide-react";
import { Pie, PieChart, Cell, ResponsiveContainer, Treemap } from "recharts";
import type {
  Asset,
  StockAsset,
  EtfAsset,
  FundAsset,
  CryptoAsset,
  AssetFundamentals as FundamentalsType,
} from "@/schemas";
import { formatLargeCurrency, formatCurrency } from "@/lib/formatters";
import { CHART_COLORS } from "@/lib/chart-colors";

interface OverviewTabProps {
  asset: Asset;
  enrichedAsset: StockAsset | EtfAsset | FundAsset | null;
  fundamentals: FundamentalsType | null;
  showKeyMetrics?: boolean;
}

const formatPercent = (value?: number): string => {
  if (value === undefined || value === null) return "-";
  return `${(value * 100).toFixed(2)}%`;
};

const formatNumber = (value?: number, decimals = 2): string => {
  if (value === undefined || value === null) return "-";
  return value.toFixed(decimals);
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

type EtfHoldingSlice = {
  name: string;
  symbol: string;
  percentage: number;
  color: string;
};

function EtfHoldingsChart({
  holdings,
}: {
  holdings?: EtfAsset["top_holdings"];
}) {
  if (!holdings || holdings.length === 0) return null;

  const chartData: EtfHoldingSlice[] = holdings
    .map((holding, index) => ({
      name: holding.name || holding.symbol || "Holding",
      symbol: holding.symbol || "",
      percentage: holding.holding_percent * 100,
      color: CHART_COLORS[index % CHART_COLORS.length],
    }))
    .sort((a, b) => b.percentage - a.percentage);

  const totalHoldings = chartData.reduce((sum, item) => sum + item.percentage, 0);
  const otherPercentage = Math.max(0, 100 - totalHoldings);
  if (otherPercentage > 0.01) {
    chartData.push({
      name: "Other",
      symbol: "Other",
      percentage: otherPercentage,
      color: "#94a3b8",
    });
  }

  const chartConfig: ChartConfig = {
    value: { label: "Weight" },
  };

  chartData.forEach((slice) => {
    chartConfig[slice.symbol || slice.name] = {
      label: slice.name,
      color: slice.color,
    };
  });

  return (
    <div className="mt-6">
      <h4 className="text-sm font-medium mb-3">Top Holdings</h4>
      <div className="flex flex-col lg:flex-row gap-4 items-center lg:items-stretch">
        <div className="relative flex-1 min-w-[220px] max-w-[320px] aspect-square p-4 mx-auto">
          <ChartContainer config={chartConfig} className="w-full h-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      hideLabel
                      formatter={(value, name) => (
                        <div className="flex items-center justify-between gap-4 min-w-[150px]">
                          <span className="text-sm">{name}</span>
                          <span className="font-mono font-medium text-sm">
                            {(value as number).toFixed(2)}%
                          </span>
                        </div>
                      )}
                    />
                  }
                />
                <Pie
                  data={chartData}
                  dataKey="percentage"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="55%"
                  outerRadius="90%"
                  strokeWidth={2}
                  stroke="hsl(var(--background))"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </div>
        <div className="min-w-[180px] flex-1 w-full max-w-[420px] mx-auto">
          <div className="flex flex-col gap-2 w-full">
            {chartData.map((holding) => (
              <div
                key={`${holding.symbol}-${holding.name}`}
                className="grid grid-cols-[minmax(0,1fr),auto] items-center gap-3 text-xs"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: holding.color }}
                  />
                  <span className="font-medium truncate">
                    {holding.symbol || holding.name}
                  </span>
                  {holding.symbol && holding.name && (
                    <span className="text-muted-foreground truncate">
                      {holding.name}
                    </span>
                  )}
                </div>
                <span className="font-mono text-right">
                  {holding.percentage.toFixed(2)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function EtfSectorAllocationChart({
  sectorWeightings,
}: {
  sectorWeightings?: EtfAsset["sector_weightings"];
}) {
  if (!sectorWeightings || Object.keys(sectorWeightings).length === 0) {
    return null;
  }

  const entries = Object.entries(sectorWeightings)
    .filter(([, weight]) => weight > 0)
    .sort(([, a], [, b]) => b - a);

  const data = entries.map(([sector, weight], index) => ({
    name: sector.replace(/_/g, " "),
    size: weight * 100,
    percentage: weight * 100,
    color: CHART_COLORS[index % CHART_COLORS.length],
    key: sector,
  }));

  const chartConfig: ChartConfig = {
    value: { label: "Weight" },
  };

  data.forEach((item) => {
    chartConfig[item.key] = { label: item.name, color: item.color };
  });

  return (
    <div className="mt-6">
      <h4 className="text-sm font-medium mb-3">Sector Allocation</h4>
      <div className="h-56 w-full">
        <ChartContainer config={chartConfig} className="w-full h-full">
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={data}
              dataKey="size"
              stroke="hsl(var(--background))"
              fill="hsl(var(--primary))"
              content={<EtfTreemapContent />}
              isAnimationActive={false}
            >
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(value, name, item) => {
                      const percentage = item.payload?.percentage ?? 0;
                      return (
                        <div className="flex items-center gap-2">
                          <span>{name}</span>
                          <span className="font-mono font-medium">
                            {percentage.toFixed(1)}%
                          </span>
                          <span className="text-muted-foreground text-xs">
                            ({(value as number).toFixed(1)}%)
                          </span>
                        </div>
                      );
                    }}
                  />
                }
              />
            </Treemap>
          </ResponsiveContainer>
        </ChartContainer>
      </div>
    </div>
  );
}

type TreemapContentProps = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  name?: string;
  color?: string;
  percentage?: number;
  payload?: { percentage?: number };
};

const EtfTreemapContent = (props: TreemapContentProps) => {
  const { x = 0, y = 0, width = 0, height = 0, name = "", color = "" } = props;
  const percentage = props.percentage ?? props.payload?.percentage ?? 0;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: color,
          stroke: "hsl(var(--background))",
          strokeWidth: 2,
        }}
      />
      {width > 30 && height > 30 && (
        <foreignObject x={x} y={y} width={width} height={height}>
          <div className="w-full h-full p-1 flex flex-col justify-between overflow-hidden">
            <span
              className="text-[10px] font-medium text-white truncate leading-tight"
              title={name}
            >
              {name}
            </span>
            {height > 50 && (
              <span className="text-[9px] text-white/90 font-mono">
                {percentage.toFixed(0)}%
              </span>
            )}
          </div>
        </foreignObject>
      )}
    </g>
  );
};

function StatCard({
  label,
  value,
  icon: Icon,
  trend,
  subValue,
}: {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  trend?: "up" | "down" | "neutral";
  subValue?: string;
}) {
  return (
    <div className="p-4 rounded-lg bg-muted/50">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <span className="text-sm text-muted-foreground">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-xl font-semibold">{value}</span>
        {trend && (
          <span
            className={`flex items-center text-sm ${
              trend === "up"
                ? "text-emerald-500"
                : trend === "down"
                  ? "text-red-500"
                  : "text-muted-foreground"
            }`}
          >
            {trend === "up" && <ArrowUpRight className="h-3 w-3" />}
            {trend === "down" && <ArrowDownRight className="h-3 w-3" />}
            {trend === "neutral" && <Minus className="h-3 w-3" />}
          </span>
        )}
      </div>
      {subValue && (
        <span className="text-xs text-muted-foreground">{subValue}</span>
      )}
    </div>
  );
}

export function OverviewTab({
  asset,
  enrichedAsset,
  fundamentals,
  showKeyMetrics = true,
}: OverviewTabProps) {
  const isCrypto = asset.type === "crypto";
  const isEtf = asset.type === "etf";
  const isFund = asset.type === "fund";
  const cryptoAsset = isCrypto ? (asset as CryptoAsset) : null;
  const etfAsset = isEtf ? (asset as EtfAsset) : null;
  const fundAsset = isFund ? (asset as FundAsset) : null;

  // Crypto Overview Section
  if (isCrypto && cryptoAsset) {
    return (
      <div className="space-y-6">
        {/* Crypto Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Coins className="h-5 w-5" />
              Crypto Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Crypto metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <StatCard
                label="Market Cap"
                value={formatLargeCurrency(
                  cryptoAsset.market_cap,
                  cryptoAsset.currency_symbol,
                )}
                icon={DollarSign}
              />
              <StatCard
                label="Circulating Supply"
                value={
                  cryptoAsset.circulating_supply
                    ? `${(cryptoAsset.circulating_supply / 1e6).toFixed(2)}M`
                    : "-"
                }
                icon={Coins}
              />
              <StatCard
                label="Max Supply"
                value={
                  cryptoAsset.max_supply
                    ? `${(cryptoAsset.max_supply / 1e6).toFixed(2)}M`
                    : "Unlimited"
                }
                icon={Coins}
              />
              {cryptoAsset.max_supply && cryptoAsset.circulating_supply && (
                <StatCard
                  label="% of Max Supply"
                  value={`${((cryptoAsset.circulating_supply / cryptoAsset.max_supply) * 100).toFixed(1)}%`}
                  icon={Percent}
                />
              )}
              <StatCard
                label="Fully Diluted Value"
                value={formatLargeCurrency(
                  cryptoAsset.fully_diluted_value,
                  cryptoAsset.currency_symbol,
                )}
                icon={DollarSign}
              />
              <StatCard
                label="24H Volume"
                value={formatLargeCurrency(
                  cryptoAsset.volume_24hr,
                  cryptoAsset.currency_symbol,
                )}
                icon={BarChart3}
              />
              <StatCard
                label="All Time High"
                value={formatCurrency(
                  cryptoAsset.all_time_high,
                  cryptoAsset.currency_symbol,
                )}
                icon={TrendingUp}
              />
              <StatCard
                label="All Time Low"
                value={formatCurrency(
                  cryptoAsset.all_time_low,
                  cryptoAsset.currency_symbol,
                )}
                icon={TrendingDown}
              />
              <StatCard
                label="52 Week High"
                value={formatCurrency(
                  cryptoAsset.fifty_two_week_high,
                  cryptoAsset.currency_symbol,
                )}
                icon={TrendingUp}
              />
              <StatCard
                label="52 Week Low"
                value={formatCurrency(
                  cryptoAsset.fifty_two_week_low,
                  cryptoAsset.currency_symbol,
                )}
                icon={TrendingDown}
              />
              <StatCard
                label="50 Day Avg"
                value={formatCurrency(
                  cryptoAsset.fifty_day_average,
                  cryptoAsset.currency_symbol,
                )}
                icon={BarChart3}
              />
              <StatCard
                label="200 Day Avg"
                value={formatCurrency(
                  cryptoAsset.two_hundred_day_average,
                  cryptoAsset.currency_symbol,
                )}
                icon={BarChart3}
              />
            </div>

            {cryptoAsset.description && (
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                {cryptoAsset.description}
              </p>
            )}

            <div className="flex flex-wrap gap-4">
              {cryptoAsset.website && (
                <a
                  href={cryptoAsset.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <Globe className="h-4 w-4" />
                  Website
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {cryptoAsset.whitepaper && (
                <a
                  href={cryptoAsset.whitepaper}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <FileText className="h-4 w-4" />
                  Whitepaper
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ETF Overview Section (additional content for ETFs)
  const etfInfoCard = isEtf && etfAsset && (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Fund Information
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {etfAsset.category && (
            <div>
              <p className="text-sm text-muted-foreground">Category</p>
              <Badge variant="outline" className="mt-1">
                {etfAsset.category}
              </Badge>
            </div>
          )}
          {etfAsset.fund_family && (
            <div>
              <p className="text-sm text-muted-foreground">Fund Family</p>
              <p className="font-medium text-sm">{etfAsset.fund_family}</p>
            </div>
          )}
          {etfAsset.net_expense_ratio !== undefined && (
            <div>
              <p className="text-sm text-muted-foreground">Expense Ratio</p>
              <p className="font-medium">
                {(etfAsset.net_expense_ratio * 100).toFixed(2)}%
              </p>
            </div>
          )}
          {etfAsset.fund_inception_date && (
            <div>
              <p className="text-sm text-muted-foreground">Inception Date</p>
              <p className="font-medium">
                {new Date(etfAsset.fund_inception_date).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <StatCard
            label="Total Assets"
            value={formatLargeCurrency(
              etfAsset.total_assets,
              etfAsset.currency_symbol,
            )}
            icon={DollarSign}
          />
          <StatCard
            label="Market Cap"
            value={formatLargeCurrency(
              etfAsset.market_cap,
              etfAsset.currency_symbol,
            )}
            icon={DollarSign}
          />
          {etfAsset.nav_price && (
            <StatCard
              label="NAV Price"
              value={formatCurrency(
                etfAsset.nav_price,
                etfAsset.currency_symbol,
              )}
              icon={DollarSign}
            />
          )}
          {etfAsset.trailing_pe && (
            <StatCard
              label="P/E Ratio"
              value={etfAsset.trailing_pe.toFixed(2)}
              icon={BarChart3}
            />
          )}
        </div>

        {/* Returns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {etfAsset.ytd_return !== undefined && (
            <StatCard
              label="YTD Return"
              value={`${etfAsset.ytd_return.toFixed(2)}%`}
              icon={TrendingUp}
              trend={
                etfAsset.ytd_return > 0
                  ? "up"
                  : etfAsset.ytd_return < 0
                    ? "down"
                    : "neutral"
              }
            />
          )}
          {etfAsset.three_year_avg_return !== undefined && (
            <StatCard
              label="3Y Avg Return"
              value={`${(etfAsset.three_year_avg_return * 100).toFixed(2)}%`}
              icon={TrendingUp}
              trend={
                etfAsset.three_year_avg_return > 0
                  ? "up"
                  : etfAsset.three_year_avg_return < 0
                    ? "down"
                    : "neutral"
              }
            />
          )}
          {etfAsset.five_year_avg_return !== undefined && (
            <StatCard
              label="5Y Avg Return"
              value={`${(etfAsset.five_year_avg_return * 100).toFixed(2)}%`}
              icon={TrendingUp}
              trend={
                etfAsset.five_year_avg_return > 0
                  ? "up"
                  : etfAsset.five_year_avg_return < 0
                    ? "down"
                    : "neutral"
              }
            />
          )}
          {etfAsset.dividend_yield !== undefined && (
            <StatCard
              label="Dividend Yield"
              value={`${(etfAsset.dividend_yield * 100).toFixed(2)}%`}
              icon={Percent}
            />
          )}
        </div>

        {/* Price Levels & Risk */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard
            label="52 Week High"
            value={formatCurrency(
              etfAsset.fifty_two_week_high,
              etfAsset.currency_symbol,
            )}
            icon={TrendingUp}
          />
          <StatCard
            label="52 Week Low"
            value={formatCurrency(
              etfAsset.fifty_two_week_low,
              etfAsset.currency_symbol,
            )}
            icon={TrendingDown}
          />
          {etfAsset.beta_3year !== undefined && (
            <StatCard
              label="Beta (3Y)"
              value={etfAsset.beta_3year.toFixed(2)}
              icon={BarChart3}
            />
          )}
        </div>

        <EtfHoldingsChart holdings={etfAsset.top_holdings} />
        <EtfSectorAllocationChart
          sectorWeightings={etfAsset.sector_weightings}
        />

        {/* Asset Classes */}
        {etfAsset.asset_classes && (
          <div className="mt-6">
            <h4 className="text-sm font-medium mb-3">Asset Allocation</h4>
            <div className="flex gap-2">
              {etfAsset.asset_classes.stock_position > 0 && (
                <Badge variant="outline">
                  Stocks:{" "}
                  {(etfAsset.asset_classes.stock_position * 100).toFixed(1)}%
                </Badge>
              )}
              {etfAsset.asset_classes.bond_position > 0 && (
                <Badge variant="outline">
                  Bonds:{" "}
                  {(etfAsset.asset_classes.bond_position * 100).toFixed(1)}%
                </Badge>
              )}
              {etfAsset.asset_classes.cash_position > 0 && (
                <Badge variant="outline">
                  Cash:{" "}
                  {(etfAsset.asset_classes.cash_position * 100).toFixed(1)}%
                </Badge>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const fundInfoCard = isFund && fundAsset && (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Fund Information
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <StatCard
            label="Net Assets"
            value={formatLargeCurrency(fundAsset.net_assets, fundAsset.currency_symbol)}
            icon={DollarSign}
          />
          <StatCard
            label="Expense Ratio"
            value={formatPercent(fundAsset.expense_ratio)}
            icon={Percent}
          />
          <StatCard
            label="Yield"
            value={formatPercent(fundAsset.yield)}
            icon={Percent}
          />
          <StatCard
            label="NAV Price"
            value={formatCurrency(fundAsset.nav_price, fundAsset.currency_symbol)}
            icon={DollarSign}
          />
          <StatCard
            label="YTD Return"
            value={formatPercent(fundAsset.ytd_return)}
            icon={TrendingUp}
          />
          <StatCard
            label="Inception Date"
            value={formatDate(fundAsset.fund_inception_date)}
            icon={FileText}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {fundAsset.category && (
            <div>
              <p className="text-sm text-muted-foreground">Category</p>
              <Badge variant="outline" className="mt-1">
                {fundAsset.category}
              </Badge>
            </div>
          )}
          {fundAsset.fund_family && (
            <div>
              <p className="text-sm text-muted-foreground">Fund Family</p>
              <p className="font-medium text-sm">{fundAsset.fund_family}</p>
            </div>
          )}
          {fundAsset.legal_type && (
            <div>
              <p className="text-sm text-muted-foreground">Legal Type</p>
              <p className="font-medium text-sm">{fundAsset.legal_type}</p>
            </div>
          )}
          {fundAsset.isin && (
            <div>
              <p className="text-sm text-muted-foreground">ISIN</p>
              <p className="font-medium text-sm">{fundAsset.isin}</p>
            </div>
          )}
        </div>

        <EtfHoldingsChart holdings={fundAsset.top_holdings} />
        <EtfSectorAllocationChart sectorWeightings={fundAsset.sector_weightings} />
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* ETF Fund Info (shown first for ETFs) */}
      {etfInfoCard}
      {fundInfoCard}

      {/* Asset Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {isFund ? "Asset Overview" : "Company Overview"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {enrichedAsset?.sector && (
              <div>
                <p className="text-sm text-muted-foreground">Sector</p>
                <Badge variant="outline" className="mt-1">
                  {enrichedAsset.sector}
                </Badge>
              </div>
            )}
            {enrichedAsset?.industry && (
              <div>
                <p className="text-sm text-muted-foreground">Industry</p>
                <p className="font-medium text-sm">{enrichedAsset.industry}</p>
              </div>
            )}
            {enrichedAsset?.country && (
              <div>
                <p className="text-sm text-muted-foreground">Country</p>
                <p className="font-medium">{enrichedAsset.country}</p>
              </div>
            )}
            {enrichedAsset?.full_time_employees && (
              <div>
                <p className="text-sm text-muted-foreground">Employees</p>
                <p className="font-medium">
                  {enrichedAsset.full_time_employees.toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {enrichedAsset?.description && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {enrichedAsset.description}
            </p>
          )}

          {enrichedAsset?.website && (
            <a
              href={enrichedAsset.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-4 text-sm text-primary hover:underline"
            >
              <Globe className="h-4 w-4" />
              {enrichedAsset.website}
            </a>
          )}
        </CardContent>
      </Card>

      {/* Key Metrics Dashboard (Stocks only) */}
      {showKeyMetrics && !isEtf && !isFund && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Key Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Valuation */}
            <h4 className="text-sm font-medium mb-3 mt-0 text-muted-foreground">Valuation</h4>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard 
                label="Market Cap" 
                value={formatLargeCurrency(enrichedAsset?.market_cap, enrichedAsset?.currency_symbol)} 
                icon={DollarSign} 
              />
              <StatCard 
                label="P/E (TTM)" 
                value={formatNumber(enrichedAsset?.trailing_pe)} 
                icon={BarChart3} 
              />
              <StatCard 
                label="P/E (Forward)" 
                value={formatNumber(enrichedAsset?.forward_pe)} 
                icon={BarChart3} 
              />
              <StatCard 
                label="Price / Sales" 
                value={formatNumber(enrichedAsset?.price_to_sales)} 
                icon={BarChart3} 
              />
            </div>

            {/* Profitability & Returns */}
            <h4 className="text-sm font-medium mb-3 text-muted-foreground">Profitability & Returns</h4>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard 
                label="ROE" 
                value={formatPercent(enrichedAsset?.return_on_equity)} 
                icon={TrendingUp} 
                trend={(enrichedAsset?.return_on_equity ?? 0) > 0.15 ? "up" : "neutral"} 
              />
              <StatCard 
                label="Profit Margin" 
                value={formatPercent(enrichedAsset?.profit_margins)} 
                icon={Percent} 
                trend={(enrichedAsset?.profit_margins ?? 0) > 0.1 ? "up" : "neutral"} 
              />
              <StatCard 
                label="Operating Margin" 
                value={formatPercent(enrichedAsset?.operating_margins)} 
                icon={Percent} 
              />
              <StatCard 
                label="Free Cash Flow" 
                value={formatLargeCurrency(enrichedAsset?.free_cashflow, enrichedAsset?.currency_symbol)} 
                icon={DollarSign} 
              />
            </div>

            {/* Dividends & Risk */}
            <h4 className="text-sm font-medium mb-3 text-muted-foreground">Dividends & Risk</h4>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard 
                label="Dividend Yield" 
                value={formatPercent(enrichedAsset?.dividend_yield)} 
                icon={Percent} 
                trend={(enrichedAsset?.dividend_yield ?? 0) > 0.02 ? "up" : "neutral"} 
              />
              <StatCard 
                label="Payout Ratio" 
                value={formatPercent(enrichedAsset?.payout_ratio)} 
                icon={Percent} 
              />
              <StatCard 
                label="Beta" 
                value={formatNumber(enrichedAsset?.beta)} 
                icon={TrendingUp} 
              />
              <StatCard 
                label="Current Ratio" 
                value={formatNumber(enrichedAsset?.current_ratio)} 
                icon={BarChart3} 
              />
            </div>
            
            {/* Render a mini dividend bar chart if dividends exist */}
            {!isEtf && fundamentals?.recent_dividends && fundamentals.recent_dividends.length > 0 && (
              <div className="mt-8 pt-6 border-t border-border">
                <h4 className="text-sm font-medium mb-4 flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  Recent Dividends
                </h4>
                <div className="flex flex-wrap gap-4">
                  {fundamentals.recent_dividends.slice(0, 5).map((dividend, idx) => (
                    <div key={idx} className="bg-muted/50 rounded-md p-3 min-w-[120px]">
                      <div className="text-xs text-muted-foreground mb-1">{formatDate(dividend.date)}</div>
                      <div className="font-semibold">{formatCurrency(dividend.amount, dividend.currency_symbol || dividend.currency)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
